import { formatCurrency, formatDisplayDate, formatInteger, formatPercent } from "../formatters";

// Row shape the KPI cards/charts/table render, mapped from the live
// runOptifundBizLayerQuery response.
export interface PerformanceRow {
  date: string;
  impressions: number;
  clicks: number;
  spend: number;
  cpc: number;
  leads: number;
  qualified: number;
  paid: number;
  income: number;
  profit: number;
  cpq: number;
  convRate: number; // percentage points, e.g. 7.55 means 7.55%
}

// Maps one row of the live query's `query_output` array (the
// performanceDailyId/WeeklyId/MonthlyId queries all share this shape - see
// OptifundDashboardEndPointLogic.runQuery, which unwraps query_output
// before this runs). The API already computes CPC/profit/
// cost_per_qualified_lead/conv_rate server-side, so those are read
// directly rather than re-derived - conv_rate comes back as a 0-1 fraction
// (qualified_leads / click_in) and is scaled to percentage points here to
// match formatPercent's expectations. The date column is named differently
// per period (activity_date for Daily, week_start for Weekly, month_start
// for Monthly), so all three are checked.
export function mapPerformanceApiRow(raw: any): PerformanceRow {
  return {
    date: raw?.activity_date ?? raw?.week_start ?? raw?.month_start,
    impressions: raw?.impressions ?? 0,
    clicks: raw?.click_in ?? 0,
    spend: raw?.spend ?? 0,
    cpc: raw?.CPC ?? 0,
    leads: raw?.leads ?? 0,
    qualified: raw?.qualified_leads ?? 0,
    paid: raw?.paid_leads ?? 0,
    income: raw?.total_income ?? 0,
    profit: raw?.profit ?? 0,
    cpq: raw?.cost_per_qualified_lead ?? 0,
    convRate: (raw?.conv_rate ?? 0) * 100,
  };
}

// Mirrors PerformanceTable.tsx's TABLE_HEADERS labels/column order and cell
// formatting exactly, so the exported CSV matches what's on screen.
export const PERFORMANCE_CSV_HEADERS = [
  "Date",
  "Impressions",
  "Click in",
  "Spend",
  "CPC",
  "Leads",
  "Qualified",
  "Paid",
  "Total income",
  "Profit",
  "Cost / qual",
  "Conv rate",
];

export function toPerformanceCsvRow(row: PerformanceRow): Array<string | number> {
  return [
    formatDisplayDate(row.date),
    formatInteger(row.impressions),
    formatInteger(row.clicks),
    formatCurrency(row.spend),
    formatCurrency(row.cpc),
    formatInteger(row.leads),
    formatInteger(row.qualified),
    formatInteger(row.paid),
    formatCurrency(row.income),
    formatCurrency(row.profit),
    formatCurrency(row.cpq),
    formatPercent(row.convRate),
  ];
}
